// Distance to the green. Lives in shared/ for the same reason handicap.ts and ties.ts
// do: it's pure, it's unit-testable, and it has to give identical answers wherever it
// runs.
//
// Nothing here touches the network. Coordinates are fetched once when a round picks its
// course and ride along in the cached map record, so distances keep working in a total
// dead zone — GPS is satellite, not cellular, and that property is the whole point.
import type { HoleGeo, LatLng } from "./types.js";

/** IUGG mean Earth radius, in yards. A sphere is good to ~0.3% against the real
 * ellipsoid, which is an order of magnitude better than the phone's own fix. */
const EARTH_RADIUS_YARDS = 6371008.8 / 0.9144;

function toRadians(deg: number): number {
  return (deg * Math.PI) / 180;
}

/**
 * Great-circle distance in yards, unrounded.
 *
 * Rounding happens at the edge (see `distancesToGreen`) rather than in here, so that
 * intermediate work — picking the furthest-back tee box, say — isn't done on numbers
 * that have already been thrown away.
 */
export function haversineYards(a: LatLng, b: LatLng): number {
  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);
  const dLat = lat2 - lat1;
  const dLng = toRadians(b.lng - a.lng);

  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  // asin form rather than atan2: identical result, and clamping guards the sqrt
  // against floating-point drift above 1 for antipodal inputs.
  return 2 * EARTH_RADIUS_YARDS * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * A point's offset from an origin, in yards east (x) and north (y).
 *
 * Flat-earth on purpose: over the few hundred yards of one golf hole the error against
 * the great-circle distance is far below the phone's own accuracy, and a plane is what
 * the caller actually wants — the hole diagram needs x/y to draw with. Not for long
 * distances; `haversineYards` is the one that measures.
 */
export function toLocalYards(origin: LatLng, point: LatLng): { x: number; y: number } {
  const yardsPerDegreeLat = (2 * Math.PI * EARTH_RADIUS_YARDS) / 360;
  return {
    x: (point.lng - origin.lng) * yardsPerDegreeLat * Math.cos(toRadians(origin.lat)),
    y: (point.lat - origin.lat) * yardsPerDegreeLat,
  };
}

/** Inverse of `toLocalYards`, for turning computed geometry back into coordinates. */
export function fromLocalYards(origin: LatLng, p: { x: number; y: number }): LatLng {
  const yardsPerDegreeLat = (2 * Math.PI * EARTH_RADIUS_YARDS) / 360;
  return {
    lat: origin.lat + p.y / yardsPerDegreeLat,
    lng: origin.lng + p.x / (yardsPerDegreeLat * Math.cos(toRadians(origin.lat))),
  };
}

/** Centroid of a ring. Undefined for an empty one, which callers must not pass. */
export function centroid(points: LatLng[]): LatLng {
  let lat = 0;
  let lng = 0;
  for (const p of points) {
    lat += p.lat;
    lng += p.lng;
  }
  return { lat: lat / points.length, lng: lng / points.length };
}

/**
 * A ring pushed outward from its own centre by a fixed distance.
 *
 * Every vertex moves the same number of yards along its own radius, so a long green
 * gains an even collar rather than a scaled-up copy of itself — scaling by a ratio would
 * put twice the band at the ends than at the sides.
 *
 * True to the offset only for a roughly convex ring, which a putting surface is. A
 * pronounced concavity would have its inner corner cut short; the result is a starting
 * point that gets dragged into shape, not a finished tracing, and the builder's vertex
 * tools exist for exactly that.
 */
export function offsetRing(points: LatLng[], yards: number): LatLng[] {
  if (points.length < 3) return points;
  const c = centroid(points);
  return points.map((p) => {
    const local = toLocalYards(c, p);
    const radius = Math.hypot(local.x, local.y);
    if (radius === 0) return p;
    const k = (radius + yards) / radius;
    return fromLocalYards(c, { x: local.x * k, y: local.y * k });
  });
}

/**
 * The tee box furthest from the green, as a single point.
 *
 * This is how the hole gets its direction now that tee *sets* deliberately aren't on the
 * map. A hole commonly has several boxes — forward and senior tees are usually separate
 * platforms — and the back one gives the longest, most representative axis. Derived from
 * geometry rather than a stored label, so nothing shifts when the club moves a marker.
 *
 * Returns null when no tee box has been traced, or when there's no green center to
 * measure from; `holePlan` falls back to the hole's bounding geometry in that case.
 */
export function backTeeCentroid(geo: HoleGeo): LatLng | null {
  if (!geo.greenCenter) return null;

  let furthest: LatLng | null = null;
  let furthestYards = -1;
  for (const shape of geo.shapes ?? []) {
    if (shape.kind !== "tee" || shape.points.length === 0) continue;
    const c = centroid(shape.points);
    const yards = haversineYards(c, geo.greenCenter);
    if (yards > furthestYards) {
      furthestYards = yards;
      furthest = c;
    }
  }
  return furthest;
}

/** How long the hole is, back tee to green. Null without both ends traced. */
export function holeLengthYards(geo: HoleGeo): number | null {
  const tee = backTeeCentroid(geo);
  return tee && geo.greenCenter ? haversineYards(tee, geo.greenCenter) : null;
}

/**
 * A point on the line of play, a given number of yards back from the green.
 *
 * What the builder's position simulator stands on. Testing a yardage means being able to
 * say "put me a hundred and thirty-seven yards out" and have the number, the framing and
 * the me-dot all follow — and the only line where that means anything is the one from
 * the tee to the green.
 *
 * Past the hole's own length it keeps going behind the tee rather than stopping, which
 * is how the off-course state gets exercised: far enough back and `isOnHole` goes false,
 * the yardage becomes a dash and the plan reframes to the whole hole.
 *
 * Falls back to due south of the green on a hole with no tee box traced yet. That is a
 * direction with no claim behind it, but a half-traced hole still has to be previewable,
 * and every distance measured from the returned point is exactly right either way.
 */
export function pointAlongPlay(geo: HoleGeo, yardsFromGreen: number): LatLng | null {
  const green = geo.greenCenter;
  if (!green) return null;

  const tee = backTeeCentroid(geo);
  if (!tee) return fromLocalYards(green, { x: 0, y: -yardsFromGreen });

  const local = toLocalYards(green, tee);
  const length = Math.hypot(local.x, local.y);
  // A tee box traced on top of the green has no direction to offer either.
  if (length === 0) return fromLocalYards(green, { x: 0, y: -yardsFromGreen });

  const k = yardsFromGreen / length;
  return fromLocalYards(green, { x: local.x * k, y: local.y * k });
}

export interface GreenDistances {
  /** null when that edge of the green hasn't been traced yet. */
  front: number | null;
  middle: number | null;
  back: number | null;
}

/**
 * Whole yards from a device position to the front, middle and back of one green.
 *
 * Front and back keep the labels the tracer gave them — they are the near and far edges
 * *along the approach*, not "nearer to you" and "further from you". Play a hole
 * backwards from behind the green and front will read longer than back, which is the
 * same thing every rangefinder and yardage book does.
 *
 * Every point is independently optional, because a map is traced over many sessions and
 * a half-finished hole has to be a legal thing to hold. Absent reads as null and the UI
 * renders nothing at all rather than a placeholder.
 *
 * These are approximate by nature — see how the accuracy of the fix is surfaced
 * alongside them in the UI. Never present them as rangefinder-exact.
 */
export function distancesToGreen(geo: HoleGeo, from: LatLng): GreenDistances {
  return {
    front: geo.greenFront ? Math.round(haversineYards(from, geo.greenFront)) : null,
    middle: geo.greenCenter ? Math.round(haversineYards(from, geo.greenCenter)) : null,
    back: geo.greenBack ? Math.round(haversineYards(from, geo.greenBack)) : null,
  };
}

export interface DerivedGreen {
  /** Where the approach line crosses the near edge of the green. */
  front: LatLng | null;
  /** Centroid of the traced outline. */
  center: LatLng;
  /** Where the approach line crosses the far edge. */
  back: LatLng | null;
}

/**
 * Front, center and back worked out from a traced green outline.
 *
 * This is the shortcut that makes the builder bearable. Drawing the green is something
 * you want to do anyway for the diagram, and once it exists the three points that
 * actually carry the yardages are not a separate judgement call — they are geometry:
 *
 *   center — the centroid of the outline
 *   front  — where the line from the tee to that center leaves the outline, near side
 *   back   — the same line, far side
 *
 * That is what "front" and "back" mean on a rangefinder: the near and far edges *along
 * the approach*, not the northernmost and southernmost points. Which is exactly why the
 * tee matters — without a direction to measure along, an outline has no front.
 *
 * Returns null when there's no green outline with at least three points. `front` and
 * `back` are null when nothing gives an approach direction (no tee box and no fairway),
 * in which case the center alone is still worth having.
 *
 * Nothing here runs at read time. The builder writes the results into the stored points,
 * where they can be nudged by hand afterwards — so distances still come only from those
 * three points, never from the outline.
 */
export function deriveGreenPoints(geo: HoleGeo): DerivedGreen | null {
  const green = (geo.shapes ?? []).find((s) => s.kind === "green" && s.points.length >= 3);
  if (!green) return null;

  const center = centroid(green.points);
  const approach = approachReference(geo, center);
  if (!approach) return { front: null, center, back: null };

  // Flat-earth around the green: a few dozen yards across, where the error is far below
  // anything a traced outline could claim anyway.
  const ring = green.points.map((p) => toLocalYards(center, p));
  const a = toLocalYards(center, approach);
  const len = Math.hypot(a.x, a.y);
  if (len === 0) return { front: null, center, back: null };

  const dir = { x: a.x / len, y: a.y / len };
  const frontHit = rayHit(ring, dir);
  const backHit = rayHit(ring, { x: -dir.x, y: -dir.y });

  return {
    front: frontHit ? fromLocalYards(center, frontHit) : null,
    center,
    back: backHit ? fromLocalYards(center, backHit) : null,
  };
}

/**
 * Something to measure the approach from.
 *
 * The furthest tee box first, because that is the hole's real line. Failing that the
 * fairway, whose far end is a good stand-in for where the shot comes from. Both are
 * derived from traced ground rather than any stored label.
 */
function approachReference(geo: HoleGeo, center: LatLng): LatLng | null {
  let best: LatLng | null = null;
  let bestYards = -1;
  for (const kind of ["tee", "fairway"] as const) {
    for (const shape of geo.shapes ?? []) {
      if (shape.kind !== kind || shape.points.length === 0) continue;
      const c = centroid(shape.points);
      const yards = haversineYards(c, center);
      if (yards > bestYards) {
        bestYards = yards;
        best = c;
      }
    }
    // Only fall through to the fairway when no tee box was traced at all.
    if (best) return best;
  }
  return best;
}

/**
 * Where a ray from the ring's center leaves the ring, in local yards.
 *
 * Takes the *furthest* crossing rather than the nearest: on a kidney-shaped green the
 * line out of the centroid can clip an inward notch on its way, and the edge anyone
 * means by "back of the green" is the outer one.
 */
function rayHit(
  ring: { x: number; y: number }[],
  dir: { x: number; y: number },
): { x: number; y: number } | null {
  // Standard ray/segment solve with the ray origin at (0,0): for segment a→b, the
  // perpendicular of the ray direction turns the intersection into two dot products.
  const perp = { x: -dir.y, y: dir.x };
  let bestT = -1;

  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]!;
    const b = ring[(i + 1) % ring.length]!;
    const edge = { x: b.x - a.x, y: b.y - a.y };

    const denom = edge.x * perp.x + edge.y * perp.y;
    if (Math.abs(denom) < 1e-12) continue; // edge runs parallel to the ray

    // v points from the segment start back to the ray origin.
    const v = { x: -a.x, y: -a.y };
    const t = (edge.x * v.y - edge.y * v.x) / denom;
    const s = (v.x * perp.x + v.y * perp.y) / denom;

    if (t >= 0 && s >= 0 && s <= 1 && t > bestT) bestT = t;
  }

  return bestT < 0 ? null : { x: dir.x * bestT, y: dir.y * bestT };
}

/**
 * Ramer-Douglas-Peucker, in yards.
 *
 * Not an optimisation. OSM traces a cart path with a vertex every few yards, and a
 * course carries around a thousand of those ways — Torrey Pines has 1,145. Imported
 * raw that is most of a megabyte of JSON in a file whose whole value is that a human
 * can read its diff and revert a bad edit, downloaded by every phone that opens the
 * course.
 */
export function simplifyPath(points: LatLng[], toleranceYards: number): LatLng[] {
  if (points.length <= 2) return points;

  const first = points[0]!;
  const last = points[points.length - 1]!;
  const a = toLocalYards(first, first);
  const b = toLocalYards(first, last);

  let worst = 0;
  let index = 0;
  for (let i = 1; i < points.length - 1; i++) {
    const p = toLocalYards(first, points[i]!);
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
    const d = Math.hypot(p.x - (a.x + dx * t), p.y - (a.y + dy * t));
    if (d > worst) {
      worst = d;
      index = i;
    }
  }

  if (worst <= toleranceYards) return [first, last];
  return [
    ...simplifyPath(points.slice(0, index + 1), toleranceYards),
    ...simplifyPath(points.slice(index), toleranceYards).slice(1),
  ];
}
